import React, { useState, useEffect } from "react";
import axios from "axios";
import { Send, Paperclip, MessageCircle, History } from "lucide-react";

export default function SupportPage() {
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [file, setFile] = useState(null);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [darkMode, setDarkMode] = useState(true);

  const [popupData, setPopupData] = useState(null); // ⭐ NEW POPUP STATE

  // realtime update
  useEffect(() => {
    const syncUser = () => {
      JSON.parse(localStorage.getItem("user") || "{}");
    };
    window.addEventListener("userUpdated", syncUser);
    return () => window.removeEventListener("userUpdated", syncUser);
  }, []);

  // fetch reports
  const fetchReports = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;

      const res = await axios.get("https://api.speedfollowinfo.com/api/reports/my", {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.data.success) setReports(res.data.reports || []);
      else setReports([]);
    } catch {
      setReports([]);
    }
  };

  useEffect(() => {
    fetchReports();
  }, []);

  // submit report
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const formData = new FormData();
    formData.append("subject", subject);
    formData.append("message", message);
    if (file) formData.append("attachment", file);

    try {
      const token = localStorage.getItem("token");
      const res = await axios.post("https://api.speedfollowinfo.com/api/reports", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      });

      if (res.data.success) {
        // clear inputs
        setSubject("");
        setMessage("");
        setFile(null);
        fetchReports();

        // ⭐ SHOW POPUP
        setPopupData({
          subject,
          message,
        });
      }
    } catch {
      alert("❌ Failed to send report!");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className={`min-h-screen transition-all duration-500 ${
        darkMode ? "bg-[#0b0f1a] text-white" : "bg-gray-100 text-black"
      }`}
    >
      {/* ⭐ POPUP OVERLAY */}
      {popupData && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-xl flex justify-center items-center z-[99999] animate-fadeIn">
          <div className="bg-white/10 backdrop-blur-2xl border border-white/20 rounded-2xl shadow-[0_0_25px_#00e6a8] p-8 w-[90%] max-w-md text-center animate-scaleIn">

            <div className="text-6xl mb-4 animate-pop">💬</div>

            <h2 className="text-3xl font-extrabold text-[#00e6a8] drop-shadow-lg mb-3">
              Report Submitted
            </h2>

            <p className="text-gray-200 text-lg mb-3">
              Your message has been successfully sent.
            </p>

            <div className="text-left bg-black/20 p-4 rounded-xl border border-white/10">
              <p>
                <b className="text-[#00e6a8]">Subject:</b> {popupData.subject}
              </p>
              <p className="mt-2">
                <b className="text-[#00e6a8]">Message:</b> {popupData.message}
              </p>
            </div>

            <button
              onClick={() => setPopupData(null)}
              className="mt-5 w-full bg-[#00e6a8] hover:bg-[#00c59b] text-black font-bold py-3 rounded-lg transition"
            >
              OK
            </button>
          </div>

          {/* Popup Animations */}
          <style>{`
            @keyframes fadeIn { from {opacity:0;} to {opacity:1;} }
            @keyframes scaleIn {
              0% { transform: scale(0.7); opacity:0; }
              100% { transform: scale(1); opacity:1; }
            }
            @keyframes pop {
              0% { transform: scale(0.5); }
              80% { transform: scale(1.25); }
              100% { transform: scale(1); }
            }
            .animate-fadeIn { animation: fadeIn 0.35s ease-out; }
            .animate-scaleIn { animation: scaleIn 0.35s ease-out; }
            .animate-pop { animation: pop 0.45s ease-out; }
          `}</style>
        </div>
      )}

      {/* Header */}
      <header
        className={`p-5 flex justify-between items-center shadow-lg ${
          darkMode ? "bg-[#10182b]" : "bg-white"
        }`}
      >
        <h1 className="text-2xl font-bold tracking-wide flex items-center gap-2">
          <MessageCircle size={26} className="text-[#00e6a8]" />
          Support Center
        </h1>

        <button
          onClick={() => setDarkMode(!darkMode)}
          className="px-3 py-2 rounded-lg text-sm font-semibold bg-[#00e6a8] text-black hover:opacity-90 transition"
        >
          {darkMode ? "☀️ Light Mode" : "🌙 Dark Mode"}
        </button>
      </header>

      {/* CONTENT */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 p-6">
        
        {/* LEFT */}
        <div
          className={`p-6 rounded-2xl shadow-xl border ${
            darkMode ? "bg-[#151c34] border-[#1e2550]" : "bg-white border-gray-300"
          }`}
        >
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            📝 New Report
          </h2>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* subject */}
            <div>
              <label className="font-medium">Subject</label>
              <input
                type="text"
                placeholder="Enter your report subject..."
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                required
                className={`w-full p-3 mt-2 rounded-lg border focus:outline-none ${
                  darkMode
                    ? "bg-[#0f1530] border-[#30365f] text-white"
                    : "bg-gray-100 border-gray-400"
                }`}
              />
            </div>

            {/* message */}
            <div>
              <label className="font-medium">Message</label>
              <textarea
                placeholder="আপনার সমস্যার কথা এখানে লিখুন..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                required
                className={`w-full p-3 mt-2 rounded-lg min-h-[120px] border focus:outline-none ${
                  darkMode
                    ? "bg-[#0f1530] border-[#30365f] text-white"
                    : "bg-gray-100 border-gray-400"
                }`}
              />
            </div>

            {/* file */}
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 cursor-pointer text-[#00e6a8] font-semibold">
                <Paperclip size={18} />
                <span>Attach File</span>
                <input
                  type="file"
                  className="hidden"
                  onChange={(e) => setFile(e.target.files[0])}
                />
              </label>
              {file && (
                <span className="text-sm opacity-80 truncate max-w-[160px]">
                  📎 {file.name}
                </span>
              )}
            </div>

            {/* submit */}
            <button
              type="submit"
              disabled={loading}
              className="w-full flex justify-center items-center gap-2 p-3 rounded-lg font-bold text-white bg-gradient-to-r from-[#00e6a8] to-[#00bcd4] hover:opacity-90 transition"
            >
              <Send size={18} />
              {loading ? "Sending..." : "Create Report"}
            </button>
          </form>
        </div>

        {/* RIGHT – HISTORY */}
        <div
          className={`p-6 rounded-2xl shadow-xl border overflow-y-auto max-h-[600px] ${
            darkMode ? "bg-[#151c34] border-[#1e2550]" : "bg-white border-gray-300"
          }`}
        >
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <History /> Report History
          </h2>

          {reports.length === 0 ? (
            <p className="text-gray-400 text-center">No reports found.</p>
          ) : (
            <div className="space-y-4">
              {reports.map((r) => (
                <div
                  key={r._id}
                  className={`p-4 rounded-xl shadow-md border ${
                    darkMode
                      ? "bg-[#0f1530] border-[#2a3060]"
                      : "bg-gray-50 border-gray-300"
                  }`}
                >
                  <h3 className="font-bold text-lg text-[#00e6a8]">{r.subject}</h3>
                  <p className="text-sm mt-1 opacity-90">{r.message}</p>

                  {r.attachment && (
                    <a
                      href={`https://api.speedfollowinfo.com${r.attachment}`}
                      target="_blank"
                      rel="noreferrer"
                      className="text-blue-400 underline text-sm mt-2 inline-block"
                    >
                      📎 View Attachment
                    </a>
                  )}

                  <div className="flex justify-between mt-3 text-xs text-gray-400">
                    <span>{r.status}</span>
                    <span>{new Date(r.createdAt).toLocaleString()}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* WhatsApp Floating */}
      <a
        href="https://wa.me/8801340277797"
        target="_blank"
        rel="noreferrer"
        className="fixed right-5 bottom-5 rounded-full w-14 h-14 flex items-center justify-center text-white shadow-lg bg-[#25D366] hover:scale-110 transition"
        title="WhatsApp"
      >
        💬
      </a>
    </div>
  );
}